/* eslint require-jsdoc: 'error', valid-jsdoc: ["error", { "requireReturn": false }] */
import Line from '@fusioncharts/charts/src/dataset/line';
import { pluck, pluckNumber, getValidValue,
  getDashStyle, getFirstValue, toRaphaelColor, getFirstColor } from '@fusioncharts/core/src/lib';
import { addDep } from '@fusioncharts/core/src/dependency-manager';
import trendsetAnimation from './trendset.animation';

let HUNDRED = '100',
  // The default value for stroke-dash attribute
  DASH_DEF = 'none';

addDep({
  name: 'trendsetAnimation',
  type: 'animationRule',
  extension: trendsetAnimation
});
/**
 * Class def for bull candles chart API
 * This dataset is reponsible for drawing candle depending on open and close value
 */
class Trendset extends Line {
  /**
   * Sets the default value of drawLine to 1. This enables trendset component to draw the line.
   *
   * @memberof Trendset
   */
  __setDefaultConfig () {
    super.__setDefaultConfig();
    this.config.drawLine = 1;
  }
  /**
    * Function for parsing all the attributes and value given by the user at chart,dataset and set level.
    * This function is called once from the init() function of the Column class.
    * @param  {Object} datasetJSON JSON for dataset configurations
    * @return {boolean} Indicates whether the configuration was successful or not
    */
  configureAttributes (datasetJSON) {
    if (!datasetJSON) {
      return false;
    }
    this.trimData(datasetJSON);
    this.config.JSONData = datasetJSON;
    var dataset = this,
      chartAttr = dataset.getFromEnv('chart-attrib'),
      trendset = dataset.config.JSONData,
      index = dataset.index,
      setDataArr = trendset.data || trendset.set || [],
      conf = dataset.config,
      dataObj,
      itemValue,
      dataStore = dataset.components.data,
      dataStoreObj,
      config,
      maxValue = -Infinity,
      minValue = +Infinity,
      x,
      length,
      xMax = -Infinity,
      xMin = +Infinity,
      NumberFormatter = dataset.getFromEnv('number-formatter'),
      // Trend-sets default properties
      trendSetColor = getFirstColor(pluck(trendset.color, chartAttr.trendsetcolor, '666666')), // eslint-disable-line good-practices/no-single-usage-variable
      trendSetAlpha = pluck(trendset.alpha, chartAttr.trendsetalpha, HUNDRED), // eslint-disable-line good-practices/no-single-usage-variable
      trendSetThickness = pluckNumber(trendset.thickness, chartAttr.trendsetthickness, 2), // eslint-disable-line good-practices/no-single-usage-variable
      trendSetDashed = Boolean(pluckNumber(trendset.dashed, chartAttr.trendsetdashed, 0)), // eslint-disable-line good-practices/no-single-usage-variable
      trendSetDashLen = pluckNumber(trendset.dashlen, chartAttr.trendsetdashlen, 4), // eslint-disable-line good-practices/no-single-usage-variable
      trendSetDashGap = pluckNumber(trendset.dashgap, chartAttr.trendsetdashgap, 4); // eslint-disable-line good-practices/no-single-usage-variable

    conf.includeInLegend = pluckNumber(trendset.includeinlegend, 1);
    conf.lineColor = trendSetColor;
    conf.lineAlpha = trendSetAlpha;
    conf.connectNullData = pluckNumber(chartAttr.connectnulldata, 0);
    conf.linethickness = trendSetThickness;
    conf.lineDashStyle = trendSetDashed ? getDashStyle(trendSetDashLen, trendSetDashGap) : DASH_DEF;
    conf.name = getValidValue(trendset.name);
    conf.includeInLegend = pluckNumber(trendset.includeinlegend, 1);
    conf.defaultPadding = {
      left: 0.5,
      right: 0.5
    };
    if (!dataStore) {
      dataStore = dataset.components.data = [];
    }
    for (index = 0, length = setDataArr.length; index < length; index += 1) { // eslint-disable-line good-practices/no-single-usage-variable
      dataObj = setDataArr[index];
      if (dataObj && !dataObj.vline) {
        dataStoreObj = dataStore[index] = dataStore[index] || (dataStore[index] = {});
        config = dataStoreObj.config = dataStoreObj.config || (dataStoreObj.config = {});
        itemValue = config._y = config.setValue = NumberFormatter.getCleanValue(dataObj.value);
        config.setColor = conf.lineColor;
        config.setAlpha = conf.lineAlpha;
        x = NumberFormatter.getCleanValue(dataObj.x);
        x = config._x = config.x = x !== null ? x : index + 1;
        maxValue = Math.max(maxValue, itemValue);
        minValue = Math.min(minValue, itemValue);
        xMin = Math.min(xMin, x);
        xMax = Math.max(xMax, x);
        // Parsing the anchor properties for set level
        config.anchorProps = this._parseAnchorProperties(index);
        config.hoverEffects = {};
        if (!dataStoreObj.graphics) {
          dataStoreObj.graphics = {};
        }
      }
    }
    conf.max = maxValue;
    conf.min = minValue;
    conf.xMax = xMax;
    conf.xMin = xMin;
    dataset.getFromEnv('chartConfig').showLegend && dataset._addLegend();
    dataset.setState('visible', true);
    dataset.setState('dirty', true);
  }
  /**
   * Function to register datasets in the internal data store of legend class.
   */
  _addLegend () {
    var dataset = this,
      conf = dataset.config,
      legendItem,
      legend = dataset.getFromEnv('legend');
    if (conf.includeinlegend) {
      legendItem = legend.getItem(dataset.config.legendItemId);
      if (!legendItem) {
        dataset.config.legendItemId = legend.createItem(dataset);
        legendItem = legend.getItem(dataset.config.legendItemId);
      } else {
        legendItem.configure({ style: legend.config.itemStyle,
          hiddenStyle: legend.config.itemHiddenStyle,
          datasetVisible: legend.config.datasetVisible,
          hoverStyle: legend.config.itemHoverStyle });
      }
      legendItem.configure({
        enabled: conf.includeInLegend,
        interactiveLegend: false,
        type: dataset.type,
        drawLine: true,
        label: getFirstValue(dataset.config.JSONData.name)
      });
      legendItem.setStateCosmetics('default', {
        symbol: {
          stroke: toRaphaelColor({
            color: conf.lineColor,
            alpha: HUNDRED
          }),
          rawFillColor: conf.lineColor
        }
      });
    } else if (dataset.config.legendItemId) {
      legend.disposeItem(dataset.config.legendItemId);
    }
  }
  /**
   * Function to get the maximum and minimum from dataset
   * @return {Object}     contains max, min value
   */
  getDataLimits () {
    var conf = this.config;
    return {
      max: conf.max,
      min: conf.min,
      xMax: conf.xMax,
      xMin: conf.xMin
    };
  }
  /**
   * Sets the type of the component
   * @return {string} type
   */
  getType () {
    return 'trendset';
  }
  /**
    * This function overrides drawlabel method
    * it prevents the label drawing
    */
  // eslint-disable-next-line
  drawLabel () {
  }

  /**
   * Sets the name of the component
   * @return {string} name
   */
  getName () {
    return 'trendset';
  }
}

export default Trendset;
